import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
    Close,
    Download,
    LoginOutlined,
    NavigateBefore,
    NavigateNext,
    ShareOutlined,
    ZoomIn,
    ZoomOut,
    CenterFocusStrong,
} from '@mui/icons-material'

const getValue = (value, item, index, fallback = '') => {
    if (typeof value === 'function') return value(item, index)
    if (value === undefined || value === null) return fallback
    return value
}

const getBoolean = (value, item, index) => {
    if (typeof value === 'function') return !!value(item, index)
    return !!value
}

const getMimeType = (item, index, resolverMime, getItemMimeType) => String(
    resolverMime ||
    getValue(getItemMimeType, item, index, '') ||
    item?.mimeType ||
    item?.fileMime ||
    ''
).toLowerCase()

const isBlobValue = (value) => (
    typeof Blob !== 'undefined' &&
    value instanceof Blob
)

const normalizeResolvedAsset = (value) => {
    if (!value) return {}

    if (typeof value === 'string') {
        return { url: value }
    }

    if (isBlobValue(value)) {
        return { blob: value }
    }

    if (typeof value === 'object') {
        return value
    }

    return {}
}

const normalizeSourceCandidates = (value) => {
    const items = Array.isArray(value)
        ? value
        : value
            ? [value]
            : []

    const seen = new Set()

    return items
        .map((item) => {
            if (!item) return null

            if (typeof item === 'string') {
                return {
                    url: item,
                    quality: '',
                }
            }

            if (typeof item !== 'object') return null

            return {
                ...item,
                url: String(item.url || ''),
                quality: String(item.quality || ''),
            }
        })
        .filter((item) => {
            const url = String(item?.url || '').trim()

            if (!url || seen.has(url)) return false

            seen.add(url)
            return true
        })
}

const getQualityLabel = (quality) => {
    switch (String(quality || '').toLowerCase()) {
        case 'original':
            return 'ОРИГИНАЛ'
        case 'optimized':
            return 'ОПТИМИЗИРОВАНО'
        case 'thumbnail':
            return 'МИНИАТЮРА'
        default:
            return ''
    }
}

const detectMediaKind = (mimeType, source = '') => {
    const mime = String(mimeType || '').toLowerCase()
    const url = String(source || '').toLowerCase()

    if (mime.startsWith('image/')) return 'image'
    if (mime === 'application/pdf') return 'pdf'
    if (mime.startsWith('video/')) return 'video'
    if (mime.startsWith('audio/')) return 'audio'

    if (/\.(png|jpe?g|gif|webp|bmp|avif)(?:$|[?#])/i.test(url)) return 'image'
    if (/\.pdf(?:$|[?#])/i.test(url)) return 'pdf'
    if (/\.(mp4|mov|webm|mkv|avi)(?:$|[?#])/i.test(url)) return 'video'
    if (/\.(ogg|oga|mp3|m4a|aac|wav|webm)(?:$|[?#])/i.test(url)) return 'audio'

    return 'image'
}

const SiteMediaViewer = ({
    open = false,
    items = [],
    activeIndex = 0,
    onActiveIndexChange,
    onClose,
    resolveItem,
    getSource,
    getFallbackSource,
    getSourceCandidates,
    getItemMimeType,
    getTitle,
    getSubtitle,
    getAlt,
    onDownload,
    downloadLabel = 'Скачать',
    downloadDisabled = false,
    onEnter,
    enterLabel = 'Войти в этот кадр',
    enterDisabled = false,
    onShare,
    shareLabel = 'Поделиться кадром',
    shareDisabled = false,
    extraActions = null,
    bottomContent = null,
    loadingText = 'Загружаем оригинал…',
    emptyText = 'Файл недоступен',
    error = '',
    showCounter = true,
    loop = true,
    dataSalesSkipClickAction = false,
}) => {
    const safeItems = useMemo(
        () => (Array.isArray(items) ? items.filter(Boolean) : []),
        [items]
    )
    const controlledIndex = safeItems.length
        ? Math.min(safeItems.length - 1, Math.max(0, Number(activeIndex) || 0))
        : 0
    const [internalIndex, setInternalIndex] = useState(controlledIndex)
    const safeIndex = safeItems.length
        ? Math.min(safeItems.length - 1, Math.max(0, internalIndex))
        : 0
    const activeItem = safeItems[safeIndex] || null

    const [asset, setAsset] = useState(null)
    const [loading, setLoading] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [sourceCandidates, setSourceCandidates] = useState([])
    const [sourceCandidateIndex, setSourceCandidateIndex] = useState(0)
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 })
    const [zoom, setZoom] = useState(100)
    const [pan, setPan] = useState({ x: 0, y: 0 })
    const [dragging, setDragging] = useState(false)
    const requestRevisionRef = useRef(0)
    const ownedObjectUrlRef = useRef('')
    const dragRef = useRef(null)

    useEffect(() => {
        if (!open) return

        setInternalIndex(controlledIndex)
    }, [controlledIndex, open, safeItems.length])

    useEffect(() => {
        if (!open) return

        setZoom(100)
        setPan({ x: 0, y: 0 })
        setDragging(false)
        setSourceCandidateIndex(0)
        setImageDimensions({ width: 0, height: 0 })
        dragRef.current = null
    }, [open, safeIndex])

    const revokeOwnedObjectUrl = useCallback(() => {
        if (!ownedObjectUrlRef.current) return
        URL.revokeObjectURL(ownedObjectUrlRef.current)
        ownedObjectUrlRef.current = ''
    }, [])

    useEffect(() => {
        if (!open) return undefined

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [open])

    useEffect(() => {
        if (!open || !activeItem) {
            requestRevisionRef.current += 1
            revokeOwnedObjectUrl()
            setAsset(null)
            setLoading(false)
            setLoadError('')
            return undefined
        }

        const revision = requestRevisionRef.current + 1
        requestRevisionRef.current = revision
        let cancelled = false

        const explicitCandidates = normalizeSourceCandidates(
            getValue(getSourceCandidates, activeItem, safeIndex, [])
        )

        const legacyPrimarySource = String(
            getValue(getSource, activeItem, safeIndex, '') || ''
        )
        const legacyFallbackSource = String(
            getValue(getFallbackSource, activeItem, safeIndex, '') || ''
        )

        const legacyCandidates = normalizeSourceCandidates([
            legacyPrimarySource ? { url: legacyPrimarySource } : null,
            legacyFallbackSource ? { url: legacyFallbackSource } : null,
        ])

        const directCandidates = explicitCandidates.length
            ? explicitCandidates
            : legacyCandidates
        const firstCandidate = directCandidates[0] || null
        const fallbackSource = firstCandidate?.url || legacyPrimarySource

        revokeOwnedObjectUrl()
        setSourceCandidates(directCandidates)
        setSourceCandidateIndex(0)
        setImageDimensions({ width: 0, height: 0 })
        setAsset(firstCandidate
            ? {
                ...firstCandidate,
                url: firstCandidate.url,
            }
            : fallbackSource
                ? { url: fallbackSource }
                : null)
        setLoadError('')

        if (typeof resolveItem !== 'function') {
            setLoading(false)

            if (!fallbackSource) {
                setLoadError(emptyText)
            }

            return undefined
        }

        setLoading(true)

        Promise.resolve(resolveItem(activeItem, safeIndex))
            .then((resolvedValue) => {
                if (cancelled || requestRevisionRef.current !== revision) return

                const resolved = normalizeResolvedAsset(resolvedValue)
                let url = String(resolved.url || '')

                if (!url && resolved.blob && isBlobValue(resolved.blob)) {
                    url = URL.createObjectURL(resolved.blob)
                    ownedObjectUrlRef.current = url
                }

                setSourceCandidates([])
                setSourceCandidateIndex(0)
                setImageDimensions({ width: 0, height: 0 })
                setAsset({
                    ...resolved,
                    url: url || fallbackSource,
                })
            })
            .catch((loadErrorValue) => {
                if (cancelled || requestRevisionRef.current !== revision) return

                setLoadError(
                    loadErrorValue?.response?.data?.message ||
                    loadErrorValue?.response?.data?.error ||
                    loadErrorValue?.message ||
                    emptyText
                )

                if (!fallbackSource) {
                    setAsset(null)
                }
            })
            .finally(() => {
                if (!cancelled && requestRevisionRef.current === revision) {
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [
        activeItem,
        emptyText,
        getFallbackSource,
        getSource,
        getSourceCandidates,
        open,
        resolveItem,
        revokeOwnedObjectUrl,
        safeIndex,
    ])

    useEffect(() => () => {
        requestRevisionRef.current += 1
        revokeOwnedObjectUrl()
    }, [revokeOwnedObjectUrl])

    const setIndex = useCallback((nextIndex) => {
        if (!safeItems.length) return

        let normalizedIndex = nextIndex

        if (loop) {
            normalizedIndex = (
                (nextIndex % safeItems.length) + safeItems.length
            ) % safeItems.length
        } else {
            normalizedIndex = Math.min(
                safeItems.length - 1,
                Math.max(0, nextIndex)
            )
        }

        setInternalIndex(normalizedIndex)
        onActiveIndexChange?.(normalizedIndex, safeItems[normalizedIndex])
    }, [loop, onActiveIndexChange, safeItems])

    const previous = useCallback(() => {
        if (safeItems.length < 2) return
        setIndex(safeIndex - 1)
    }, [safeIndex, safeItems.length, setIndex])

    const next = useCallback(() => {
        if (safeItems.length < 2) return
        setIndex(safeIndex + 1)
    }, [safeIndex, safeItems.length, setIndex])

    const clampZoom = useCallback((value) => {
        const numeric = Number(value)

        if (!Number.isFinite(numeric)) return 100

        return Math.min(400, Math.max(50, Math.round(numeric)))
    }, [])

    const applyZoom = useCallback((value) => {
        const nextZoom = clampZoom(value)

        setZoom(nextZoom)

        if (nextZoom <= 100) {
            setPan({ x: 0, y: 0 })
        }
    }, [clampZoom])

    const zoomIn = useCallback(() => {
        applyZoom(zoom + 10)
    }, [applyZoom, zoom])

    const zoomOut = useCallback(() => {
        applyZoom(zoom - 10)
    }, [applyZoom, zoom])

    const resetZoom = useCallback(() => {
        setZoom(100)
        setPan({ x: 0, y: 0 })
        setDragging(false)
        dragRef.current = null
    }, [])

    const handleImageWheel = useCallback((event) => {
        event.preventDefault()

        if (event.deltaY < 0) {
            applyZoom(zoom + 10)
        } else if (event.deltaY > 0) {
            applyZoom(zoom - 10)
        }
    }, [applyZoom, zoom])

    const handleImagePointerDown = useCallback((event) => {
        if (zoom <= 100 || event.button !== 0) return
        if (event.target?.closest?.('button, input, a, audio, video')) return

        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            panX: pan.x,
            panY: pan.y,
        }

        event.currentTarget.setPointerCapture?.(event.pointerId)
        setDragging(true)
    }, [pan.x, pan.y, zoom])

    const handleImagePointerMove = useCallback((event) => {
        const drag = dragRef.current

        if (!drag || drag.pointerId !== event.pointerId) return

        setPan({
            x: drag.panX + (event.clientX - drag.startX),
            y: drag.panY + (event.clientY - drag.startY),
        })
    }, [])

    const stopImageDrag = useCallback((event) => {
        const drag = dragRef.current

        if (!drag) return
        if (event?.pointerId !== undefined && drag.pointerId !== event.pointerId) return

        try {
            event?.currentTarget?.releasePointerCapture?.(drag.pointerId)
        } catch (_error) {
            // Pointer capture уже мог быть снят браузером.
        }

        dragRef.current = null
        setDragging(false)
    }, [])

    const handleImageDoubleClick = useCallback(() => {
        if (zoom === 100) {
            applyZoom(200)
            return
        }

        resetZoom()
    }, [applyZoom, resetZoom, zoom])

    useEffect(() => {
        if (!open) return undefined

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                onClose?.()
                return
            }

            if (event.key === 'ArrowLeft' && safeItems.length > 1) {
                event.preventDefault()
                previous()
            }

            if (event.key === 'ArrowRight' && safeItems.length > 1) {
                event.preventDefault()
                next()
                return
            }

            if (event.key === '+' || event.key === '=') {
                event.preventDefault()
                zoomIn()
                return
            }

            if (event.key === '-' || event.key === '_') {
                event.preventDefault()
                zoomOut()
                return
            }

            if (event.key === '0') {
                event.preventDefault()
                resetZoom()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [
        next,
        onClose,
        open,
        previous,
        resetZoom,
        safeItems.length,
        zoomIn,
        zoomOut,
    ])

    if (!open) return null

    const source = String(asset?.url || '')
    const mimeType = getMimeType(activeItem, safeIndex, asset?.mimeType, getItemMimeType)
    const mediaKind = detectMediaKind(mimeType, source)
    const title = String(
        getValue(getTitle, activeItem, safeIndex, 'Просмотр файла') ||
        'Просмотр файла'
    )
    const subtitle = String(
        getValue(getSubtitle, activeItem, safeIndex, '') || ''
    )
    const alt = String(
        getValue(getAlt, activeItem, safeIndex, title) || title
    )
    const visibleError = String(error || loadError || '')
    const canNavigate = safeItems.length > 1

    const downloadBlocked = getBoolean(downloadDisabled, activeItem, safeIndex)
    const enterBlocked = getBoolean(enterDisabled, activeItem, safeIndex)
    const shareBlocked = getBoolean(shareDisabled, activeItem, safeIndex)
    const resolvedDownloadLabel = String(
        getValue(downloadLabel, activeItem, safeIndex, 'Скачать') || 'Скачать'
    )
    const resolvedEnterLabel = String(
        getValue(enterLabel, activeItem, safeIndex, 'Войти в этот кадр') ||
        'Войти в этот кадр'
    )
    const resolvedShareLabel = String(
        getValue(shareLabel, activeItem, safeIndex, 'Поделиться кадром') ||
        'Поделиться кадром'
    )
    const activeSourceCandidate = sourceCandidates[sourceCandidateIndex] || null
    const activeQuality = String(
        asset?.quality ||
        activeSourceCandidate?.quality ||
        ''
    ).toLowerCase()
    const qualityLabel = getQualityLabel(activeQuality)
    const dimensionsLabel = imageDimensions.width && imageDimensions.height
        ? `${imageDimensions.width}×${imageDimensions.height}`
        : ''
    const qualityDisplay = [qualityLabel, dimensionsLabel]
        .filter(Boolean)
        .join(' · ')

    const handleMediaImageLoad = (event) => {
        const img = event.currentTarget

        setLoadError('')
        setImageDimensions({
            width: Number(img.naturalWidth || 0),
            height: Number(img.naturalHeight || 0),
        })
    }

    const handleMediaImageError = () => {
        const nextIndex = sourceCandidateIndex + 1
        const nextCandidate = sourceCandidates[nextIndex]

        setImageDimensions({ width: 0, height: 0 })

        if (nextCandidate?.url) {
            setSourceCandidateIndex(nextIndex)
            setAsset({
                ...nextCandidate,
                url: nextCandidate.url,
            })
            setLoadError('')
            return
        }

        setAsset(null)
        setLoadError(emptyText)
    }

    const renderMedia = () => {
        if (loading && !source) {
            return (
                <div className="scene-screenshot-preview__loading">
                    <span className="scene-screenshots-gallery__spinner" />
                    {loadingText}
                </div>
            )
        }

        if (visibleError && !source) {
            return (
                <div className="scene-screenshot-preview__loading is-error">
                    {visibleError}
                </div>
            )
        }

        if (!source) {
            return (
                <div className="scene-screenshot-preview__loading">
                    {emptyText}
                </div>
            )
        }

        if (mediaKind === 'pdf') {
            return (
                <iframe
                    className="scene-screenshot-preview__media is-pdf"
                    src={source}
                    title={title}
                />
            )
        }

        if (mediaKind === 'video') {
            return (
                <video
                    className="scene-screenshot-preview__media is-video"
                    src={source}
                    controls
                    preload="metadata"
                />
            )
        }

        if (mediaKind === 'audio') {
            return (
                <div className="scene-screenshot-preview__fileState">
                    <strong>{title}</strong>
                    <audio
                        className="scene-screenshot-preview__media is-audio"
                        src={source}
                        controls
                        preload="metadata"
                    />
                </div>
            )
        }

        return (
            <img
                key={`${safeIndex}:${source}`}
                className="scene-screenshot-preview__media is-image"
                src={source}
                alt={alt}
                draggable="false"
                onLoad={handleMediaImageLoad}
                onError={handleMediaImageError}
                onDoubleClick={handleImageDoubleClick}
                style={{
                    transform: `translate(-50%, -50%) translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom / 100})`,
                }}
            />
        )
    }

    if (typeof document === 'undefined') return null

    const viewerNode = (
        <div
            className="scene-screenshot-preview"
            data-sales-skip-click-action={dataSalesSkipClickAction ? 'true' : undefined}
            role="dialog"
            aria-modal="true"
            aria-label={title}
        >
            <div
                className="scene-screenshot-preview__backdrop"
                onClick={onClose}
            />

            <div className="scene-screenshot-preview__panel">
                <div className="scene-screenshot-preview__topbar">
                    <div>
                        <strong>{title}</strong>
                        {subtitle ? <span>{subtitle}</span> : null}
                    </div>

                    <div className="scene-screenshot-preview__actions">
                        {mediaKind === 'image' && source ? (
                            <div
                                className="scene-screenshot-preview__zoom"
                                aria-label="Масштаб фотографии"
                            >
                                <button
                                    type="button"
                                    className="scene-screenshot-preview__zoomButton"
                                    onClick={zoomOut}
                                    disabled={zoom <= 50}
                                    title="Уменьшить (-)"
                                    aria-label="Уменьшить"
                                >
                                    <ZoomOut />
                                </button>

                                <input
                                    className="scene-screenshot-preview__zoomRange"
                                    type="range"
                                    min="50"
                                    max="400"
                                    step="10"
                                    value={zoom}
                                    onChange={(event) => applyZoom(event.target.value)}
                                    aria-label="Масштаб фотографии"
                                />

                                <label className="scene-screenshot-preview__zoomValue">
                                    <input
                                        type="number"
                                        min="50"
                                        max="400"
                                        step="10"
                                        value={zoom}
                                        onChange={(event) => applyZoom(event.target.value)}
                                        aria-label="Масштаб в процентах"
                                    />
                                    <span>%</span>
                                </label>

                                <button
                                    type="button"
                                    className="scene-screenshot-preview__zoomButton"
                                    onClick={zoomIn}
                                    disabled={zoom >= 400}
                                    title="Увеличить (+)"
                                    aria-label="Увеличить"
                                >
                                    <ZoomIn />
                                </button>

                                <button
                                    type="button"
                                    className="scene-screenshot-preview__zoomFit"
                                    onClick={resetZoom}
                                    title="Сбросить масштаб (0)"
                                    aria-label="Сбросить масштаб"
                                >
                                    <CenterFocusStrong />
                                </button>
                            </div>
                        ) : null}

                        {onDownload ? (
                            <button
                                type="button"
                                onClick={() => onDownload(activeItem, safeIndex)}
                                disabled={downloadBlocked}
                            >
                                <Download />
                                {resolvedDownloadLabel}
                            </button>
                        ) : null}

                        {onEnter ? (
                            <button
                                type="button"
                                className="is-enter"
                                onClick={() => onEnter(activeItem, safeIndex)}
                                disabled={enterBlocked}
                            >
                                <LoginOutlined />
                                {resolvedEnterLabel}
                            </button>
                        ) : null}

                        {onShare ? (
                            <button
                                type="button"
                                onClick={() => onShare(activeItem, safeIndex)}
                                disabled={shareBlocked}
                            >
                                <ShareOutlined />
                                {resolvedShareLabel}
                            </button>
                        ) : null}

                        {typeof extraActions === 'function'
                            ? extraActions(activeItem, safeIndex)
                            : extraActions}

                        <button
                            type="button"
                            className="scene-screenshot-preview__close"
                            onClick={onClose}
                            aria-label="Закрыть просмотр"
                        >
                            <Close />
                        </button>
                    </div>
                </div>

                <div
                    className={[
                        'scene-screenshot-preview__imageStage',
                        mediaKind === 'image' ? 'is-image-stage' : '',
                        mediaKind === 'image' && zoom > 100 ? 'is-zoomed' : '',
                        mediaKind === 'image' && dragging ? 'is-dragging' : '',
                    ].filter(Boolean).join(' ')}
                    onWheel={mediaKind === 'image' ? handleImageWheel : undefined}
                    onPointerDown={mediaKind === 'image' ? handleImagePointerDown : undefined}
                    onPointerMove={mediaKind === 'image' ? handleImagePointerMove : undefined}
                    onPointerUp={mediaKind === 'image' ? stopImageDrag : undefined}
                    onPointerCancel={mediaKind === 'image' ? stopImageDrag : undefined}
                >
                    {mediaKind === 'image' && qualityDisplay ? (
                        <div
                            className={[
                                'scene-screenshot-preview__qualityBadge',
                                activeQuality ? `is-${activeQuality}` : '',
                            ].filter(Boolean).join(' ')}
                            title="Фактически загруженное качество изображения"
                        >
                            {qualityDisplay}
                        </div>
                    ) : null}

                    <button
                        type="button"
                        className="scene-screenshot-preview__nav is-prev"
                        onClick={previous}
                        disabled={!canNavigate}
                        aria-label="Предыдущий файл"
                    >
                        <NavigateBefore />
                    </button>

                    {renderMedia()}

                    {loading && source ? (
                        <div className="scene-screenshot-preview__loadingOverlay">
                            <span className="scene-screenshots-gallery__spinner" />
                        </div>
                    ) : null}

                    <button
                        type="button"
                        className="scene-screenshot-preview__nav is-next"
                        onClick={next}
                        disabled={!canNavigate}
                        aria-label="Следующий файл"
                    >
                        <NavigateNext />
                    </button>
                </div>

                {showCounter && safeItems.length ? (
                    <div className="scene-screenshot-preview__counter">
                        {safeIndex + 1} из {safeItems.length}
                    </div>
                ) : null}

                {typeof bottomContent === 'function'
                    ? bottomContent(activeItem, safeIndex)
                    : bottomContent}
            </div>
        </div>
    )

    return createPortal(viewerNode, document.body)
}

export default React.memo(SiteMediaViewer)
